import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { companyAPI } from '../api';

// ── Default theme (matches CSS variables in index.css) ───────
export const THEME_DEFAULTS = {
  theme_header_bg:               '#f4c70b',
  theme_header_text:             '#ffffff',
  theme_header_svg:              '#ffffff',
  theme_sidebar_bg:              '#ffffff',
  theme_sidebar_links:           '#4a5568',
  theme_sidebar_svg:             '#4a5568',
  theme_sidebar_active_item:     '#1a56db',
  theme_sidebar_active_item_bg:  '#1a56db',
  theme_sidebar_svg_hover:       '#1a56db',
  theme_sidebar_svg_active_mini: '#1a56db',
  theme_topnav_hover_bg:         '#1a56db',
  theme_sidebar_active_bg:       '#eff4ff',
};

export const THEME_LABELS = {
  theme_header_bg:               'Top Header Background Color',
  theme_header_text:             'Top Header Text Color',
  theme_header_svg:              'Top Header Svg Fill',
  theme_sidebar_bg:              'Sidebar/Menu/Setup Menu Background Color',
  theme_sidebar_links:           'Sidebar/Menu/Setup Menu Links Color',
  theme_sidebar_svg:             'Sidebar/Menu/svg color',
  theme_sidebar_active_item:     'Sidebar Menu/Setup Active Item Color',
  theme_sidebar_active_item_bg:  'Sidebar Menu Svg Active Color',
  theme_sidebar_svg_hover:       'Sidebar Menu Svg Text Mini Hover Color',
  theme_sidebar_svg_active_mini: 'Sidebar Menu Svg Text Active Mini Color',
  theme_topnav_hover_bg:         'Top Nav Menu Hover Background Color',
  theme_sidebar_active_bg:       'Sidebar/Menu/Setup Active Item Background Color',
};

// Map theme keys → CSS custom property names
const CSS_VAR_MAP = {
  theme_header_bg:               '--theme-header-bg',
  theme_header_text:             '--theme-header-text',
  theme_header_svg:              '--theme-header-svg',
  theme_sidebar_bg:              '--theme-sidebar-bg',
  theme_sidebar_links:           '--theme-sidebar-links',
  theme_sidebar_svg:             '--theme-sidebar-svg',
  theme_sidebar_active_item:     '--theme-sidebar-active',
  theme_sidebar_active_item_bg:  '--theme-sidebar-active-bg',
  theme_sidebar_svg_hover:       '--theme-sidebar-hover',
  theme_sidebar_svg_active_mini: '--theme-sidebar-active-mini',
  theme_topnav_hover_bg:         '--theme-topnav-hover-bg',
  theme_sidebar_active_bg:       '--theme-sidebar-active-item-bg',
};

function applyThemeToDom(theme) {
  const root = document.documentElement;
  Object.entries(CSS_VAR_MAP).forEach(([key, cssVar]) => {
    root.style.setProperty(cssVar, theme[key] || THEME_DEFAULTS[key]);
  });
}

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('iassets_theme');
      return saved ? { ...THEME_DEFAULTS, ...JSON.parse(saved) } : { ...THEME_DEFAULTS };
    } catch { return { ...THEME_DEFAULTS }; }
  });
  const [loading, setLoading] = useState(false);

  // Apply on mount and whenever theme changes
  useEffect(() => { applyThemeToDom(theme); }, [theme]);

  // Load from API when user is logged in — fire and forget, never blocks the app
  const loadTheme = useCallback(() => {
    companyAPI.getTheme()
      .then(({ data }) => {
        const flat = {};
        Object.entries(data.data).forEach(([k, v]) => { flat[k] = v.value; });
        const merged = { ...THEME_DEFAULTS, ...flat };
        setTheme(merged);
        localStorage.setItem('iassets_theme', JSON.stringify(merged));
      })
      .catch(() => {
        // API unavailable — localStorage/default theme already applied, nothing to do
      });
  }, []);

  const saveTheme = useCallback(async (newTheme) => {
    const merged = { ...theme, ...newTheme };
    setTheme(merged);
    localStorage.setItem('iassets_theme', JSON.stringify(merged));
    applyThemeToDom(merged);
    await companyAPI.saveTheme(merged);
  }, [theme]);

  const resetTheme = useCallback(async () => {
    setTheme({ ...THEME_DEFAULTS });
    localStorage.setItem('iassets_theme', JSON.stringify(THEME_DEFAULTS));
    applyThemeToDom(THEME_DEFAULTS);
    await companyAPI.saveTheme(THEME_DEFAULTS);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, loading, loadTheme, saveTheme, resetTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
